import type {
  HTMLAttributes,
  ReactNode,
} from 'react'

import { cn } from '@/shared/lib/cn'

export interface PageHeaderProps
  extends Omit<
    HTMLAttributes<HTMLElement>,
    'title'
  > {
  title: ReactNode
  description?: ReactNode
  eyebrow?: ReactNode

  leadingContent?: ReactNode
  actions?: ReactNode
}

export function PageHeader({
  title,
  description,
  eyebrow,
  leadingContent,
  actions,
  className,
  ...headerProps
}: PageHeaderProps) {
  return (
    <header
      {...headerProps}
      className={cn(
        [
          'flex flex-col gap-4',
          'sm:flex-row sm:items-start',
          'sm:justify-between',
        ],
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-4">
        {leadingContent ? (
          <div className="shrink-0">
            {leadingContent}
          </div>
        ) : null}

        <div className="min-w-0">
          {eyebrow ? (
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-(--color-primary)">
              {eyebrow}
            </div>
          ) : null}

          <h1 className="text-2xl font-semibold tracking-[-0.025em] text-(--color-foreground) sm:text-[1.75rem]">
            {title}
          </h1>

          {description ? (
            <p className="mt-1.5 max-w-3xl text-sm leading-6 text-(--color-muted-foreground)">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      ) : null}
    </header>
  )
}
